import { ReactNode, useEffect, useState } from 'react';

// third-party
import { IntlProvider, MessageFormatElement } from 'react-intl';

// project-imports
import useConfig from 'hooks/useConfig';
import { I18n } from 'types/config';

type Messages = Record<string, string> | Record<string, MessageFormatElement[]>;

type Catalog = () => Promise<{ default: Messages }>;

// Each screen group keeps its strings in its own catalogue, merged here per language. Adding a screen
// means adding a file to its language's list rather than growing one file that every change touches.
const CATALOGS: Record<I18n, Catalog[]> = {
  ru: [
    () => import('utils/locales/ru.json'),
    () => import('utils/locales/ru.shared.json'),
    () => import('utils/locales/ru.developer.json'),
    () => import('utils/locales/ru.platform.json'),
    () => import('utils/locales/ru.news.json'),
    () => import('utils/locales/ru.segments.json'),
    () => import('utils/locales/ru.models.json'),
    () => import('utils/locales/ru.payments.json'),
    () => import('utils/locales/ru.errors.json'),
    () => import('utils/locales/ru.landing.json'),
    () => import('utils/locales/ru.legal.json'),
    () => import('utils/locales/ru.docs.json')
  ],
  en: [
    () => import('utils/locales/en.json'),
    () => import('utils/locales/en.shared.json'),
    () => import('utils/locales/en.developer.json'),
    () => import('utils/locales/en.platform.json'),
    () => import('utils/locales/en.news.json'),
    () => import('utils/locales/en.segments.json'),
    () => import('utils/locales/en.models.json'),
    () => import('utils/locales/en.payments.json'),
    () => import('utils/locales/en.errors.json'),
    () => import('utils/locales/en.landing.json'),
    () => import('utils/locales/en.legal.json'),
    () => import('utils/locales/en.docs.json')
  ],
  uz: [
    () => import('utils/locales/uz.json'),
    () => import('utils/locales/uz.shared.json'),
    () => import('utils/locales/uz.developer.json'),
    () => import('utils/locales/uz.platform.json'),
    () => import('utils/locales/uz.news.json'),
    () => import('utils/locales/uz.segments.json'),
    () => import('utils/locales/uz.models.json'),
    () => import('utils/locales/uz.payments.json'),
    () => import('utils/locales/uz.errors.json'),
    () => import('utils/locales/uz.landing.json'),
    () => import('utils/locales/uz.legal.json'),
    () => import('utils/locales/uz.docs.json')
  ]
};

const loadMessages = async (locale: I18n): Promise<Messages> => {
  const parts = await Promise.all((CATALOGS[locale] ?? CATALOGS.ru).map((load) => load()));

  return Object.assign({}, ...parts.map((part) => part.default));
};

// ==============================|| LOCALIZATION ||============================== //

interface Props {
  children: ReactNode;
}

const Locales = ({ children }: Props) => {
  const { i18n } = useConfig();

  const [messages, setMessages] = useState<Messages | undefined>();

  useEffect(() => {
    let active = true;

    loadMessages(i18n).then((loaded) => {
      if (active) setMessages(loaded);
    });

    return () => {
      active = false;
    };
  }, [i18n]);

  return (
    <>
      {messages && (
        <IntlProvider locale={i18n} defaultLocale="ru" messages={messages}>
          {children}
        </IntlProvider>
      )}
    </>
  );
};

export default Locales;
